import { buildAbsoluteCanonicalUrl } from './canonical-url.util';
import { LISTING_SEO_SITE_NAME } from './listing-seo.util';

export interface SiteSeoEntry {
  /** Route prefix to match, e.g. `/home` or `/featured-properties`. */
  path: string;
  title: string;
  description: string;
  /** Canonical path relative to site origin. Defaults to `path`. */
  canonicalPath?: string;
  /** Root-relative Open Graph / Twitter image path. */
  ogImagePath?: string;
  /** Optional structured data for the page. */
  jsonLdBuilder?: (siteOrigin: string) => Record<string, unknown>;
}

export function buildHomeLocalBusinessJsonLd(siteOrigin: string): Record<string, unknown> {
  const origin = siteOrigin.replace(/\/$/, '');

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: LISTING_SEO_SITE_NAME,
    url: `${origin}/home`,
    image: `${origin}/assets/images/heroes/home-laredo-aerial.jpg`,
    areaServed: [
      { '@type': 'City', name: 'Laredo' },
      { '@type': 'City', name: 'Zapata' },
      { '@type': 'AdministrativeArea', name: 'South Texas' },
    ],
    description:
      'Gabali Realty helps buyers and sellers with homes, land, commercial, and industrial property in Laredo and South Texas.',
  };
}

export const SITE_SEO_PAGES: SiteSeoEntry[] = [
  {
    path: '/home',
    title: `Gabali Realty | Laredo & South Texas Real Estate`,
    description:
      'Search homes, land, commercial, and industrial listings in Laredo, Zapata, and South Texas. Local agents for buying, selling, and investing with Gabali Realty.',
    canonicalPath: '/home',
    ogImagePath: '/assets/images/heroes/home-laredo-aerial.jpg',
    jsonLdBuilder: buildHomeLocalBusinessJsonLd,
  },
  {
    path: '/featured-properties',
    title: `Homes & Properties For Sale | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Browse active homes, land, commercial, and industrial listings on the Gabali Realty map and list search.',
    canonicalPath: '/featured-properties',
  },
  {
    path: '/for-rent',
    title: `Featured Rentals | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Browse featured rental listings curated by Gabali Realty in Laredo and South Texas.',
    canonicalPath: '/for-rent',
  },
  {
    path: '/agents',
    title: `Meet Our Agents | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Work with local Laredo and South Texas real estate experts at Gabali Realty.',
    canonicalPath: '/agents',
  },
  {
    path: '/buyer-guide',
    title: `Buyer Guide | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Step-by-step home buying guide for Laredo and South Texas — budgeting, map search, local market tips, and expert agent support from Gabali Realty.',
    canonicalPath: '/buyer-guide',
  },
  {
    path: '/seller-guide',
    title: `Seller Guide | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Step-by-step home selling guide for Laredo and South Texas — pricing, prep, marketing, and expert agent support from Gabali Realty.',
    canonicalPath: '/seller-guide',
  },
  {
    path: '/contact-page',
    title: `Contact Us | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Send a message to Gabali Realty for help buying, selling, or leasing property in Laredo and South Texas.',
    canonicalPath: '/contact-page',
  },
  {
    path: '/home-estimate-page',
    title: `Home Estimate | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Request a home value estimate and connect with a Gabali Realty agent in Laredo and South Texas.',
    canonicalPath: '/home-estimate-page',
  },
  {
    path: '/zestimate',
    title: `Property Value Lookup | ${LISTING_SEO_SITE_NAME}`,
    description:
      'Look up a property address and explore buying or selling resources with Gabali Realty.',
    canonicalPath: '/zestimate',
  },
];

const LISTING_DETAIL_PREFIXES = [
  '/featured-properties/listing/',
  '/for-rent/listing/',
];

export function isListingDetailPath(urlPath: string): boolean {
  return LISTING_DETAIL_PREFIXES.some((prefix) => urlPath.includes(prefix));
}

export function resolveSiteSeoEntry(urlPath: string): SiteSeoEntry | null {
  const normalized = normalizePath(urlPath);

  if (!normalized || isListingDetailPath(normalized)) {
    return null;
  }

  const sorted = [...SITE_SEO_PAGES].sort((left, right) => right.path.length - left.path.length);

  for (const entry of sorted) {
    if (normalized === entry.path || normalized.startsWith(`${entry.path}/`)) {
      return entry;
    }
  }

  if (normalized === '/') {
    return SITE_SEO_PAGES.find((entry) => entry.path === '/home') ?? null;
  }

  return null;
}

export function buildSiteCanonicalUrl(siteOrigin: string, entry: SiteSeoEntry): string {
  const path = entry.canonicalPath ?? entry.path;
  return buildAbsoluteCanonicalUrl(siteOrigin, path);
}

function normalizePath(urlPath: string): string {
  const pathOnly = urlPath.split('?')[0].split('#')[0];
  if (!pathOnly || pathOnly === '/') {
    return '/';
  }

  return pathOnly.endsWith('/') && pathOnly.length > 1
    ? pathOnly.slice(0, -1)
    : pathOnly;
}
